import React, { useMemo, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, Tooltip, Button } from 'antd';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';

import {
  ControlOutlined,
  FileSearchOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { bytesFormat, dateFormat, formatCurrency } from '@utils/formatters';

import useSearchParams from '@hooks/useSearchParams';
import { useNavigate } from 'react-router-dom';
import SelectCustomers from '@components/SelectCustomers';
import SessionContext from '@providers/Session/SessionContext';
import { ProductsConstants } from '@utils/applicationsConstants';
import CustomTag from '@components/CustomTag';
import { IContract } from '@components/DeviceActionModal/types';
import { ContractActions } from './types';
import ContractActionsModal from './ContractActionsModal/index';
import ContractActionsMenu from './ContractActionsMenu';
import CustomerChangeNickNameModal from './CustomerChangeNickNameModal';

const { Search } = Input;

const CustomersContracts = () => {
  const { t } = useTranslation(['customer-contracts', 'inventory', 'main']);
  const { isRoot } = useContext(SessionContext);

  const [actionToExecute, setActionToExecute] = useState<ContractActions>();
  const [modalActionVisible, setModalActionVisible] = useState<boolean>(false);
  const [tableSelection, setTableSelection] = useState<any>();
  const [productValue, setProductValue] = useState('');
  const [flagBlockCRRValue, setFlagBlockCRRValue] = useState('');
  const [flagVisibleValue, setFlagVisibleValue] = useState('');
  const [flagIscaValue, setFlagIscaValue] = useState('');

  const [statusValue, setStatusValue] = useState('');
  const [nickNameValue, setNickNameValue] = useState('');

  const navigate = useNavigate();

  const { data: contracts } = useSwr<IContract[]>('/service-proxy/contracts');

  const contractsOptions = useMemo(
    () =>
      Array.isArray(contracts)
        ? contracts?.map(({ assetNumber, planName, child }) => {
            const labelParts = [assetNumber, planName].filter(Boolean);
            const label = labelParts.join(' - ');

            if (child?.length > 0) {
              return {
                label,
                options: child?.map(childItem => {
                  const childLabelParts = [
                    childItem?.assetNumber,
                    childItem?.acronym,
                    childItem?.planName,
                  ].filter(Boolean);
                  const childLabel = childLabelParts.join(' - ');
                  return {
                    label: childLabel,
                    value: childItem?.assetNumber,
                  };
                }),
              };
            }

            return {
              label,
              value: assetNumber,
            };
          })
        : undefined,
    [contracts],
  );

  const {
    setSearchParams,
    clearSearchParams,
    searchParams,
    hasValuesInExtraParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'contractId',
      'cpfCnpj',
      'flagIsca',
      'customerId',
      'product',
      'flagBlockCRR',
      'flagVisible',
      'status',
      'dtUpdateContract',
      'nickname',
    ],
  });

  const {
    contractId,
    cpfCnpj,
    customerId,
    product,
    flagBlockCRR,
    flagIsca,
    flagVisible,
    status,
    nickname,
    page,
    linesPerPage,
    dtUpdateContract,
  } = useMemo(() => searchParams, [searchParams]);

  const filters = useMemo(() => {
    const allFilters = {
      assetNumber: contractId,
      cpfCnpj,
      customerId,
      product,
      flagBlockCRR,
      flagIsca,
      flagVisible,
      status,
      nickname,
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      dtUpdateContract,
    };

    return allFilters;
  }, [
    contractId,
    cpfCnpj,
    flagIsca,
    customerId,
    product,
    flagBlockCRR,
    flagVisible,
    status,
    nickname,
    dtUpdateContract,
    page,
    linesPerPage,
  ]);

  const { data, mutate } = useSwr('/service-proxy/contracts/customer', {
    params: {
      ...filters,
      ...(!isRoot && { flagVisible: '1' }),
    },
  });

  const dataContracts = useMemo(
    () => (data ? data?.content : undefined),
    [data],
  );

  const columns = useMemo(
    () => [
      {
        title: t('table.number'),
        dataIndex: 'assetNumber',
      },
      ...(isRoot
        ? [
            {
              title: t('table.client'),
              dataIndex: 'customerName',
            },
            {
              title: (
                <>
                  {t('table.activation')}
                  <Tooltip title={t('table.activationHelp')}>
                    <Button
                      type="link"
                      icon={
                        <QuestionCircleOutlined style={{ color: '#fff' }} />
                      }
                    />
                  </Tooltip>
                </>
              ),
              dataIndex: 'flagVisible',
              align: 'center' as const,
              render: (value: '0' | '1') => {
                const isEnabled = value === '1';
                return (
                  <CustomTag color={isEnabled ? 'green' : 'red'}>
                    {t(isEnabled ? 'enabled' : 'disabled')}
                  </CustomTag>
                );
              },
            },
          ]
        : []),
      {
        title: t('inventory:table.isca'),
        dataIndex: 'flagIsca',
        align: 'center' as const,
        render: (str: string) => {
          return str === '1'
            ? t('inventory:iscaOptions.yes')
            : t('inventory:iscaOptions.no');
        },
      },
      {
        title: t('table.type'),
        dataIndex: 'product',
        align: 'center' as const,
        render: (value: string) => t(`main:apps.${value?.toLowerCase()}`),
      },
      {
        title: t('table.iotTechnology'),
        dataIndex: 'iotTechnology',
        align: 'center' as const,
      },
      {
        title: t('inventory:table.nickname'),
        dataIndex: 'nickname',
      },
      {
        title: t('table.crr-status'),
        dataIndex: 'flagBlockCRR',
        align: 'center' as const,
        render: (value: string) => {
          const isEnabled = value === '1';
          return (
            value && (
              <CustomTag color={isEnabled ? 'green' : 'red'}>
                {t(value === '1' ? 'allowed' : 'not-allowed')}
              </CustomTag>
            )
          );
        },
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        align: 'center' as const,
        render: (value: string) =>
          value && (
            <CustomTag color={value === 'ATIVO' ? 'green' : 'orange'}>
              {value}
            </CustomTag>
          ),
      },
      {
        title: t('table.update-date'),
        dataIndex: 'dtUpdateContract',
        render: (value: string) => dateFormat(value),
      },
      {
        title: `${t('table.consumption')}/${t('table.franchise')}`,
        dataIndex: 'consumption',
        align: 'center' as const,
        render: (value: string, record: any) => {
          const prod = record?.product;
          const isConnectivity = [
            ProductsConstants.MULTI_CONNECT,
            ProductsConstants.CONNECT_ONE,
          ].includes(prod?.toLowerCase());
          const isAPI = prod === 'API';

          if (isConnectivity) {
            return `${bytesFormat(value)} / ${bytesFormat(
              record?.franchiseTotal,
            )}`;
          }

          if (isAPI) {
            return `${value ?? 0} / ${record?.franchise?.franchiseTotal ?? 0}`;
          }

          return <></>;
        },
      },
      {
        title: t('table.limiter.label'),
        align: 'center' as const,
        dataIndex: 'limiter',
        render: (val: any, record: any) => {
          if (!val) return <></>;

          const isActive = val === 'Ativo';
          return (
            <>
              <CustomTag color={isActive ? 'green' : 'red'}>
                {t(`table.limiter.${isActive ? 'active' : 'inactive'}`)}
              </CustomTag>
              {bytesFormat(record?.limitingValue)}
            </>
          );
        },
      },
      {
        title: t('table.limiter.costOfExcessMegabytes'),
        align: 'center' as const,
        dataIndex: 'costOfExcessMegabytes',
        render: (val: any, record: any) =>
          record?.limiter && formatCurrency(val),
      },
      {
        title: t('table.actions'),
        dataIndex: 'actions',
        render: (val: any, record: any) => (
          <>
            <Button
              style={{ padding: '0 8px' }}
              onClick={() => {
                navigate(`/financial/contracts/${record.assetNumber}`);
              }}
            >
              <FileSearchOutlined />
            </Button>
            <CustomerChangeNickNameModal
              idToAdd={record.id}
              nickName={record.nickname ?? ''}
              key={record.id}
              mutate={() => mutate('/service-proxy/contracts/customer')}
            />
          </>
        ),
      },
    ],
    [t, isRoot, navigate, mutate],
  );

  const productsOptions = useMemo(
    () =>
      [...Object.values(ProductsConstants), 'api'].map(productType => ({
        label: t(`main:apps.${productType}`),
        value: productType?.toUpperCase(),
      })),
    [t],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('table.number'),
        element: (
          <Select
            options={contractsOptions}
            value={contractId}
            onChange={value => setSearchParams({ contractId: value })}
            style={{ width: '100%' }}
            showSearch
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('inventory:table.nickname'),
        element: (
          <Search
            onSearch={
              (value: string) => setSearchParams({ nickname: value })
              // eslint-disable-next-line react/jsx-curly-newline
            }
            onChange={({ target: { value } }) => setNickNameValue(value)}
            value={nickNameValue}
            allowClear
          />
        ),
      },
      ...(isRoot
        ? [
            {
              visible: true,
              label: t('table.client'),
              element: (
                <SelectCustomers
                  value={customerId && Number(customerId)}
                  onChange={value => setSearchParams({ customerId: value })}
                />
              ),
            },
          ]
        : []),
      {
        visible: true,
        label: t('filters.contract-type'),
        element: (
          <Select
            style={{ width: '100%' }}
            onSelect={(value: any) => {
              setSearchParams({
                product: value,
              });
            }}
            value={productValue}
            showSearch
            options={productsOptions}
            allowClear
            onClear={() => {
              setSearchParams({
                product: undefined,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        label: t('table.status'),
        element: (
          <Select
            style={{ width: '100%' }}
            onSelect={(value: any) => {
              setSearchParams({
                status: value,
              });
            }}
            value={statusValue}
            showSearch
            options={[
              { label: t('filters.active'), value: 'ATIVO' },
              {
                label: t('filters.waiting-activation'),
                value: 'waiting-activation',
              },
              { label: t('filters.cancelled'), value: 'CANCELADO' },
              { label: t('filters.inactive'), value: 'INATIVO' },
            ]}
            allowClear
            onClear={() => {
              setSearchParams({
                status: undefined,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        label: t('inventory:table.isca'),
        element: (
          <Select
            style={{ width: '100%' }}
            onSelect={(value: any) => {
              setSearchParams({
                flagIsca: value,
              });
            }}
            value={flagIscaValue}
            showSearch
            options={[
              { label: t('inventory:iscaOptions.yes'), value: '1' },
              {
                label: t('inventory:iscaOptions.no'),
                value: '0',
              },
            ]}
            allowClear
            onClear={() => {
              setSearchParams({
                flagIsca: undefined,
              });
            }}
          />
        ),
      },
      {
        visible: isRoot,
        label: t('table.activation'),
        element: (
          <Select
            style={{ width: '100%' }}
            onSelect={(value: any) => {
              setSearchParams({
                flagVisible: value,
              });
            }}
            value={flagVisibleValue}
            showSearch
            options={[
              { label: t('enabled'), value: 'true' },
              { label: t('disabled'), value: 'false' },
            ]}
            allowClear
            onClear={() => {
              setSearchParams({
                flagVisible: undefined,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        label: t('filters.crr-block'),
        element: (
          <Select
            style={{ width: '100%' }}
            onSelect={(value: any) => {
              setSearchParams({
                flagBlockCRR: value,
              });
            }}
            value={flagBlockCRRValue}
            showSearch
            options={[
              { label: t('not-allowed'), value: 'false' },
              { label: t('allowed'), value: 'true' },
            ]}
            allowClear
            onClear={() => {
              setSearchParams({
                flagBlockCRR: undefined,
              });
            }}
          />
        ),
      },
    ],
    [
      t,
      contractsOptions,
      contractId,
      nickNameValue,
      isRoot,
      customerId,
      productValue,
      productsOptions,
      statusValue,
      flagIscaValue,
      flagVisibleValue,
      flagBlockCRRValue,
      setSearchParams,
    ],
  );

  useEffect(() => {
    setProductValue(product);
  }, [product]);

  useEffect(() => {
    setFlagBlockCRRValue(flagBlockCRR);
  }, [flagBlockCRR]);
  useEffect(() => {
    setFlagVisibleValue(flagVisible);
  }, [flagVisible]);

  useEffect(() => {
    setStatusValue(status);
  }, [status]);

  useEffect(() => {
    setFlagIscaValue(flagIsca);
  }, [flagIsca]);

  useEffect(() => {
    setNickNameValue(nickname);
  }, [nickname]);

  return (
    <div>
      <Table
        customRowKey="assetNumber"
        title={t('title')}
        columns={columns}
        data={dataContracts || []}
        loading={!dataContracts}
        tableKey="customersContracts"
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        selectable={isRoot}
        onTableChange={setSearchParams}
        setSelectedRowKeys={
          (e: any, rowData: any) => setTableSelection(rowData)
          // eslint-disable-next-line react/jsx-curly-newline
        }
        extraActions={
          <>
            {isRoot && (
              <ContractActionsMenu
                tableSelectionIds={tableSelection}
                button={
                  <Tooltip
                    title={
                      tableSelection?.length === 0 || !tableSelection
                        ? t('no-contracts')
                        : t('table.actions')
                    }
                  >
                    <Button
                      icon={<ControlOutlined />}
                      disabled={tableSelection?.length === 0 || !tableSelection}
                    />
                  </Tooltip>
                }
                onActionClick={({ key }) => {
                  setActionToExecute(key);
                  setModalActionVisible(true);
                }}
              />
            )}
          </>
        }
        expandable={{
          expandedRowRender: (record: any) => (
            <div className="expandable-table-content">
              {record?.child?.map((item: any) => {
                const childLabelParts = [
                  item?.assetNumber,
                  item?.acronym,
                  item?.planName,
                ].filter(Boolean);
                const childLabel = childLabelParts.join(' - ');
                return <p style={{ margin: 0 }}>{childLabel}</p>;
              })}
            </div>
          ),
          rowExpandable: (record: any) => record?.child?.length > 0,
        }}
      />

      <ContractActionsModal
        action={actionToExecute}
        visibleState={[modalActionVisible, setModalActionVisible]}
        onFinish={() => {
          mutate();
        }}
        isOpen={modalActionVisible}
        onClose={() => setModalActionVisible(false)}
        selectedContracts={modalActionVisible ? tableSelection : undefined}
      />
    </div>
  );
};

export default CustomersContracts;
